import {
	faBars,
	faDoorOpen,
	faMagnifyingGlass,
	faRotate,
	faRulerCombined,
	faTimes,
	faWarning,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn, useMediaQuery } from "@tosize/ui";
import { Fragment, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useCabinetStore } from "../../context/StoreContext";
import { useCanvasStore } from "../../hooks/useCanvasStore";
import { usePrice } from "../../hooks/usePrice";
import FloatingButton from "../FloatingButton";
import { useDebug } from "../renderer/debug";
import HistoryState from "./HistoryState";

export default function CanvasVisual() {
	const {
		showDimensions,
		setShowDimensions,
		allCompartmentsOpen,
		setAllCompartmentsOpen,
		altControls,
		setAltControls,
	} = useCanvasStore();

	const isTouchDevice = useMediaQuery({ pointer: "touch" });
	const { state } = useCabinetStore();
	const { debugEnabled } = useDebug();
	const price = usePrice();
	const intl = useIntl();

	const [open, onOpenChange] = useState(false);

	const hasDoors = useMemo(
		() => Array.from(state.compartments.values()).some((compartment) => compartment.door),
		[state.compartments],
	);

	return (
		<Fragment>
			{debugEnabled && (
				<div className="absolute top-2 left-2">
					<div className="rounded-xl bg-danger px-2 py-px font-bold text-white text-xs">
						Debug enabled
					</div>
				</div>
			)}

			<div className="absolute top-4 right-4 flex flex-col items-end space-y-2 xl:top-auto xl:bottom-[calc(var(--order-y,195px)+2rem)]">
				<FloatingButton
					label={intl.formatMessage({
						defaultMessage: "Menu openen / sluiten",
						id: "lt3ckl",
					})}
					icon={open ? faTimes : faBars}
					size="small"
					onClick={() => onOpenChange(!open)}
					active={open}
					className="xl:hidden"
				/>

				<FloatingButton
					label={intl.formatMessage({
						defaultMessage: "Deurtjes openen / sluiten",
						id: "TOHnvU",
					})}
					icon={faDoorOpen}
					size="small"
					onClick={() => setAllCompartmentsOpen(!allCompartmentsOpen)}
					active={allCompartmentsOpen}
					className={cn({ "[@media(max-width:1279px)]:hidden": !open, hidden: !hasDoors })}
				/>

				<FloatingButton
					label={intl.formatMessage({
						defaultMessage: "Afmetingen tonen / verbergen",
						id: "dqlkTc",
					})}
					icon={faRulerCombined}
					size="small"
					onClick={() => setShowDimensions(!showDimensions)}
					active={showDimensions}
					className={cn({ "[@media(max-width:1279px)]:hidden": !open })}
				/>

				<FloatingButton
					label={
						isTouchDevice
							? intl.formatMessage({
									defaultMessage: "Draaien aanzetten / uitzetten",
									id: "Yo648W",
								})
							: intl.formatMessage({
									defaultMessage: "Zoom aanzetten / uitzetten",
									id: "AYAEHs",
								})
					}
					icon={isTouchDevice ? faRotate : faMagnifyingGlass}
					size="small"
					onClick={() => setAltControls(!altControls)}
					active={altControls}
					className={cn({ "[@media(max-width:1279px)]:hidden": !open })}
				/>

				<HistoryState />

				{price.variant === "error" && (
					<a
						href="#order-furniture"
						className="-top-2 absolute right-[calc(36px+0.5rem+36px+0.5rem+36px+0.5rem)] flex h-9 w-9 flex-row items-center justify-center space-x-2 rounded-full bg-warning enter:bg-warning-medium text-white shadow outline-none motion-safe:transition-colors xl:hidden"
					>
						<FontAwesomeIcon icon={faWarning} />
					</a>
				)}
			</div>
		</Fragment>
	);
}

export function CanvasVisualExpandedView() {
	const intl = useIntl();
	const { state } = useCabinetStore();
	const { showDimensions, setShowDimensions, allCompartmentsOpen, setAllCompartmentsOpen } =
		useCanvasStore();

	const hasDoors = useMemo(
		() => Array.from(state.compartments.values()).some((compartment) => compartment.door),
		[state.compartments],
	);

	return (
		<div className="absolute top-0 right-0 flex flex-col items-end space-y-2">
			<FloatingButton
				label={intl.formatMessage({
					defaultMessage: "Deurtjes openen / sluiten",
					id: "TOHnvU",
				})}
				icon={faDoorOpen}
				size="small"
				onClick={() => setAllCompartmentsOpen(!allCompartmentsOpen)}
				active={allCompartmentsOpen}
				className={cn({ hidden: !hasDoors })}
			/>

			<FloatingButton
				label={intl.formatMessage({
					defaultMessage: "Afmetingen tonen / verbergen",
					id: "dqlkTc",
				})}
				icon={faRulerCombined}
				size="small"
				onClick={() => setShowDimensions(!showDimensions)}
				active={showDimensions}
			/>
		</div>
	);
}
